<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import PageComponent from '@/ai_studio/components/layout/PageComponent.vue'
import { useSnackbarStore } from '@/ai_studio/stores/snackbar'

const snackbar = useSnackbarStore()
const isActive = ref(false)
const loading = ref(false)
const cardLoading = ref({})

const cards = ref([
  {
    code: 'email_generation',
    image: require('@/ai_studio/assets/email-generation.png'),
    title: 'Modelo de email',
    description: 'Crie e-mails automaticamente com IA a partir de um contexto ou objetivo, gerando mensagens claras, personalizadas e prontas para envio.',
    enabled: false,
    message: 'Geração de modelos de e-mail', 
  },
  {
    code: 'sms_generation',
    image: require('@/ai_studio/assets/sms-generation.png'),
    title: 'Modelo de SMS',
    description: 'Gere mensagens SMS personalizadas com IA, definindo o objetivo, o limite de caracteres e a opção de evitar caracteres especiais para reduzir o custo de envio.',
    enabled: false,
    message: 'Geração de modelos de SMS', 
  },
  {
    code: 'call_script_generation',
    image: require('@/ai_studio/assets/call-script-generation.png'),
    title: 'Script de ligação',
    description: 'Crie scripts de ligação com IA a partir do perfil do cliente ou lead, objetivo da chamada e tom desejado, garantindo conversas mais eficientes e personalizadas.',
    enabled: false,
    message: 'Geração de scripts de ligação', 
  },
  {
    code: 'image_generation',
    image: require('@/ai_studio/assets/image-generation.png'),
    title: 'Geração de imagens',
    description: 'Gere imagens com IA direto no modelo de e-mail, a partir de um contexto ou objetivo visuais personalizados e prontos para destacar sua mensagem.',
    enabled: false,
    message: 'Geração de imagens', 
  }
])

onMounted(async () => {
  try {
    loading.value = true
    const { data } = await axios.get('/api/assistents/content-generation/integrations')
    
    cards.value.forEach(card => {
      const group = data.groups[card.code]
      if (group) card.enabled = group.all_active
    })
    
    isActive.value = cards.value.some(c => c.enabled)
  } catch (error) {
    console.error('Erro ao buscar integrações:', error)
  } finally {
    loading.value = false
  }
})

async function toggleAll(status) {
  try {
    loading.value = true
    await axios.patch('/api/assistents/content-generation/integrations/bulk-toggle', { status })
    
    cards.value.forEach(card => card.enabled = status)
    isActive.value = status
    
    snackbar.success(`Geração de conteúdo ${status ? 'ativada' : 'desativada'} com sucesso.`)
  } catch (error) {
    snackbar.error('Erro ao atualizar geração de conteúdo.')
  } finally {
    loading.value = false
  }
}

async function toggleCard(card) {
  try {
    cardLoading.value[card.code] = true
    await axios.patch(`/api/assistents/content-generation/integrations/${card.code}/toggle`, {
      status: card.enabled
    })
    
    snackbar.success(`${card.message} ${card.enabled ? 'ativada' : 'desativada'} com sucesso.`)
  } catch (error) {
    snackbar.error(`Erro ao ${card.enabled ? 'ativar' : 'desativar'} a ${card.message}.`)
    card.enabled = !card.enabled
  } finally {
    cardLoading.value[card.code] = false
  }
}
</script>

<template>
  <PageComponent
    title="Geração de conteúdo"
    subtitle="Ative ou desative os modelos de geração de conteúdo com IA."
    link="https://crmrubeus.zendesk.com/hc/pt-br/articles/41667515027085-AI-Studio-Geração-de-conteúdo"
    :show-switch="true"
    :model-value="isActive"
    @update:model-value="toggleAll"
    enabled-text="Habilitado"
    disabled-text="Desabilitado"
  >
    <div class="content-generation-cards d-flex ga-6">
      <div 
        v-for="card in cards" 
        :key="card.code"
        class="content-generation-cards__card"
      >
        <img :src="card.image" :alt="card.title" />
        <div class="content-generation-cards__card-content">
          <div class="content-generation-cards__card-content-text">
            <h4>{{ card.title }}</h4>
            <p>{{ card.description }}</p>
          </div>
          <div
            class="content-generation-cards__card-switch d-flex ga-2 align-center"
            :class="{ 'is-disabled': !isActive }"
          >
            <rb-switch
              v-model="card.enabled"
              @update:model-value="toggleCard(card)"
              :disabled="!isActive || loading || cardLoading[card.code]"
              density="compact"
            ></rb-switch>
            <span>{{ card.enabled ? 'Habilitado' : 'Desabilitado' }}</span>
          </div>
        </div>
      </div>
    </div>
  </PageComponent>
</template>

<style lang="scss" scoped>
.content-generation-cards {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  &__card {
    border: 1px solid rgb(var(--v-theme-border-neutral-primary));
    width: 284px;
    height: 380px;
    border-radius: 8px;
    text-align: start;
    line-height: 20px;
    font-family: Roboto, sans-serif;
    color: #000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 160px;
      object-fit: cover;
    }

    &-content {
      flex: 1;
      padding: 16px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      &-text {
        display: flex;
        flex-direction: column;
        gap: 4px;

        h4 {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }

        p {
          font-size: 14px;
          font-weight: 400;
          margin: 0;
        }
      }
    }

    &-switch {
      font-size: 14px;
      font-weight: 400;
      position: relative;
      transition: opacity 0.2s ease;

      &.is-disabled {
        pointer-events: none;
        opacity: 0.8;

        &::after {
          content: '';
          position: absolute;
          inset: 0;
          background-color: rgba(251, 253, 252, 0.4)
        }
      }
    }
  }
}
</style>